import React, { useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { api } from "../api";
import Select from "react-select";
import sdcLogo from "../assets/sdc-logo.png";
import authorizedSignature from "../assets/MPS.png";
import "./ProformaInvoicePage.css";

const COMPANY_INFO = {
  SDC: {
    name: "Sheth Design Consultants LLP",
    address:
      "302, Union Trade Center, Near Apple Hospital, Udhna Darwaja, Surat - 395007",
    email: "financesdcllp@gmail.com",
    phone: "9898608595",
    gstin: "24ACFFS3107B1ZR",
  },
};

const UNIT_OPTIONS = [
  "Sq. Ft.",
  "Sq. M.",
  "Running Ft.",
  "Running M.",
  "Acre",
  "Yard",
  "Nos.",
  "Hours",
  "Days",
  "Lump Sum",
  "Percentage",
];

const emptyItem = (section = "Architecture") => ({
  section,
  service: "",
  serviceName: "",
  serviceCode: "",
  description: "",
  quantity: 1,
  unit: "Sq. Ft.",
  rate: 0,
  discountType: "none",
  discountValue: 0,
  gstRate: 18,
  hsnCode: "",
});

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);

const formatAddress = (address) => {
  if (!address) return "";

  if (typeof address === "string") {
    return address;
  }

  if (typeof address === "object") {
    return [
      address.address,
      address.city,
      address.state,
      address.country,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  return "";
};

const cleanSectionName = (value = "") =>
  String(value).trim().replace(/\s+/g, " ");

const getServiceId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
};

const SEARCH_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderColor: state.isFocused ? "#94a3b8" : "#dbe3ee",
    boxShadow: state.isFocused ? "0 0 0 1px #94a3b8" : "none",
    fontSize: 13,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    "&:hover": {
      borderColor: "#94a3b8",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: 36,
    padding: "0 10px",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 36,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 999999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
    fontSize: 13,
  }),
};

const SearchableSelect = ({
  value,
  options,
  onChange,
  placeholder = "Select",
  isClearable = true,
  menuPortalTarget = document.body,
}) => {
  const selectedOption =
    options.find((option) => String(option.value) === String(value ?? "")) ||
    null;

  return (
    <Select
      value={selectedOption}
      options={options}
      onChange={(option) => onChange(option?.value ?? "")}
      placeholder={placeholder}
      isSearchable
      isClearable={isClearable}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      noOptionsMessage={({ inputValue }) =>
        inputValue ? `No result for "${inputValue}"` : "No options"
      }
      styles={SEARCH_SELECT_STYLES}
    />
  );
};

const ProformaInvoicePage = () => {
  const previewRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [banks, setBanks] = useState([]);
  const [termsMaster, setTermsMaster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfAutoSaving, setPdfAutoSaving] = useState(false);
  const [pdfAutoSaveDoneFor, setPdfAutoSaveDoneFor] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [activeServiceSection, setActiveServiceSection] =
    useState("Architecture");
  const [showFloatingAddService, setShowFloatingAddService] = useState(false);
  const activeAddServiceButtonRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  // List period controls - Indian Financial Year (April to March)
  const currentFilterDate = new Date();

  const getCurrentFinancialYearStart = () => {
    const now = new Date();
    const year = now.getFullYear();
    return now.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearForDate = (dateValue) => {
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    return date.getMonth() + 1 >= 4 ? year : year - 1;
  };

  const getFinancialYearLabel = (startYear) =>
    `${startYear}-${String(Number(startYear) + 1).slice(-2)}`;

  const getCalendarYearForFinancialMonth = (financialYearStart, month) =>
    Number(month) >= 4
      ? Number(financialYearStart)
      : Number(financialYearStart) + 1;

  const [filterBy, setFilterBy] = useState("month");
  const [filterFinancialYear, setFilterFinancialYear] = useState(
    String(getCurrentFinancialYearStart()),
  );
  const [filterMonth, setFilterMonth] = useState(
    String(currentFilterDate.getMonth() + 1),
  );
  const [filterQuarter, setFilterQuarter] = useState(
    currentFilterDate.getMonth() + 1 >= 4
      ? String(Math.floor((currentFilterDate.getMonth() + 1 - 4) / 3) + 1)
      : "4",
  );
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterMinTotal, setFilterMinTotal] = useState("");
  const [filterMaxTotal, setFilterMaxTotal] = useState("");
  const [form, setForm] = useState({
    company: "SDC",
    client: "",
    projectName: "",
    subject: "",
    documentDate: today(),
    dueDate: "",
    taxMode: "CGST_SGST",
    discountMode: "item",
    overallDiscountType: "none",
    overallDiscountValue: 0,
    bank: "",
    items: [emptyItem()],
    terms: [],
    status: "Draft",
    paidAmount: 0,
  });

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    }),
    [],
  );

  const load = async () => {
    try {
      setLoading(true);
      const [d, c, p, s, b, t] = await Promise.all([
        api.get("/sales/documents?type=proforma", { headers }),
        api.get("/sales/clients", { headers }),
        api.get("/projects", { headers }),
        api.get("/sales/services", { headers }),
        api.get("/sales/banks", { headers }),
        api.get("/sales/terms", { headers }),
      ]);

      setDocs(d.data || []);
      setClients(c.data || []);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setServices(s.data || []);
      setBanks(b.data || []);
      setTermsMaster(t.data || []);
    } catch (error) {
      console.error("Failed to load proforma invoice data:", error);
      alert(
        error.response?.data?.message || "Failed to load proforma invoice data",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => c._id === form.client),
    [clients, form.client],
  );

  const selectedProject = useMemo(
    () => projects.find((project) => project.name === form.projectName),
    [projects, form.projectName],
  );

  const filteredProjects = useMemo(() => {
    if (!selectedClient) return projects;

    const selectedClientName = String(selectedClient.companyName || "")
      .trim()
      .toLowerCase();

    if (!selectedClientName) return projects;

    const matchedProjects = projects.filter(
      (project) =>
        String(project.clientName || "")
          .trim()
          .toLowerCase() === selectedClientName,
    );

    return matchedProjects.length ? matchedProjects : projects;
  }, [projects, selectedClient]);

  const filteredBanks = useMemo(
    () => banks.filter((b) => b.company === form.company),
    [banks, form.company],
  );

  const selectedBank = useMemo(
    () => banks.find((b) => b._id === form.bank),
    [banks, form.bank],
  );

  const companyInfo = COMPANY_INFO[form.company] || COMPANY_INFO.SDC;

  const sections = useMemo(() => {
    const names = [];

    (form.items || []).forEach((item) => {
      const section = cleanSectionName(item.section || "General");
      if (section && !names.includes(section)) {
        names.push(section);
      }
    });

    return names.length ? names : ["Architecture"];
  }, [form.items]);

  useEffect(() => {
    // Floating Add Service is only needed in Edit Form mode.
    if (!open || showPreview) {
      setShowFloatingAddService(false);
      return undefined;
    }

    const originalButton = activeAddServiceButtonRef.current;

    if (!originalButton) {
      setShowFloatingAddService(false);
      return undefined;
    }

    const updateFloatingButton = () => {
      const rect = originalButton.getBoundingClientRect();

      // Show only when the original Add Service button has moved above
      // the visible screen because the user scrolled down.
      setShowFloatingAddService(rect.bottom < 0);
    };

    updateFloatingButton();

    const observer = new IntersectionObserver(
      () => {
        updateFloatingButton();
      },
      {
        root: null,
        threshold: [0, 1],
      },
    );

    observer.observe(originalButton);
    window.addEventListener("scroll", updateFloatingButton, true);
    window.addEventListener("resize", updateFloatingButton);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", updateFloatingButton, true);
      window.removeEventListener("resize", updateFloatingButton);
    };
  }, [open, showPreview, activeServiceSection, form.items.length]);

  const automaticTermsBySection = useMemo(() => {
    return sections
      .map((section) => {
        const normalizedSection = cleanSectionName(section);

        const matchedRows = termsMaster.filter(
          (row) =>
            String(row.service || "")
              .trim()
              .toLowerCase() === normalizedSection.toLowerCase(),
        );

        const terms = Array.from(
          new Set(
            matchedRows.flatMap((row) =>
              Array.isArray(row.terms) ? row.terms : [],
            ),
          ),
        ).filter(Boolean);

        return {
          section: normalizedSection,
          title: matchedRows[0]?.title || "Terms & Conditions",
          terms,
        };
      })
      .filter((group) => group.terms.length);
  }, [sections, termsMaster]);

  const automaticTerms = useMemo(
    () =>
      Array.from(
        new Set(
          automaticTermsBySection.flatMap((sectionGroup) => sectionGroup.terms),
        ),
      ),
    [automaticTermsBySection],
  );

  const totals = useMemo(() => {
    let subtotal = 0;
    let itemDiscount = 0;
    let itemTaxBeforeOverall = 0;

    const mode = form.discountMode || "item";

    (form.items || []).forEach((item) => {
      const qty = Number(item.quantity || 0);
      const rate = Number(item.rate || 0);
      const base = qty * rate;

      let discount = 0;

      // Item discount is applied ONLY when Item Wise mode is selected.
      if (mode === "item") {
        if (item.discountType === "percentage") {
          discount = (base * Number(item.discountValue || 0)) / 100;
        } else if (item.discountType === "amount") {
          discount = Number(item.discountValue || 0);
        }
      }

      discount = Math.min(Math.max(discount, 0), base);

      const taxable = base - discount;
      const tax =
        form.taxMode === "NONE"
          ? 0
          : (taxable * Number(item.gstRate || 0)) / 100;

      subtotal += base;
      itemDiscount += discount;
      itemTaxBeforeOverall += tax;
    });

    const afterItemDiscount = subtotal - itemDiscount;

    let overallDiscount = 0;

    // Total invoice discount is applied ONLY when Total Discount mode is selected.
    if (mode === "total") {
      if (form.overallDiscountType === "percentage") {
        overallDiscount =
          (afterItemDiscount * Number(form.overallDiscountValue || 0)) / 100;
      } else if (form.overallDiscountType === "amount") {
        overallDiscount = Number(form.overallDiscountValue || 0);
      }
    }

    overallDiscount = Math.min(Math.max(overallDiscount, 0), afterItemDiscount);

    const taxableAmount = Math.max(0, afterItemDiscount - overallDiscount);

    // Overall discount proportionally reduces the taxable value and therefore GST.
    const ratio = afterItemDiscount > 0 ? taxableAmount / afterItemDiscount : 1;

    const taxTotal = form.taxMode === "NONE" ? 0 : itemTaxBeforeOverall * ratio;

    const cgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const sgst = form.taxMode === "CGST_SGST" ? taxTotal / 2 : 0;

    const igst = form.taxMode === "IGST" ? taxTotal : 0;

    return {
      subtotal,
      itemDiscount,
      overallDiscount,
      discountTotal: itemDiscount + overallDiscount,
      taxableAmount,
      taxTotal,
      cgst,
      sgst,
      igst,
      grandTotal: taxableAmount + taxTotal,
    };
  }, [form]);

  const reset = () => {
    setEditing(null);
    setShowPreview(false);
    setShowFloatingAddService(false);
    setActiveServiceSection("Architecture");
    setForm({
      company: "SDC",
      client: "",
      projectName: "",
      subject: "",
      documentDate: today(),
      dueDate: "",
      taxMode: "CGST_SGST",
      discountMode: "item",
      overallDiscountType: "none",
      overallDiscountValue: 0,
      bank: "",
      items: [emptyItem()],
      terms: [],
      status: "Draft",
      paidAmount: 0,
    });
  };

  const updateItem = (index, patch) => {
    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const addSection = () => {
    const name = cleanSectionName(newSectionName);

    if (!name) {
      alert("Enter section name, for example Architecture or Interior");
      return;
    }

    if (
      sections.some((section) => section.toLowerCase() === name.toLowerCase())
    ) {
      alert("This section already exists");
      return;
    }

    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(name)],
    }));
    setActiveServiceSection(name);
    setNewSectionName("");
  };

  const addServiceToSection = (section) => {
    const targetSection =
      cleanSectionName(section) ||
      cleanSectionName(activeServiceSection) ||
      sections[0] ||
      "Architecture";

    setActiveServiceSection(targetSection);

    setForm((previous) => ({
      ...previous,
      items: [...previous.items, emptyItem(targetSection)],
    }));

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const matchingSection = Array.from(
          document.querySelectorAll("[data-service-section]"),
        ).find(
          (node) => node.getAttribute("data-service-section") === targetSection,
        );

        const cards = matchingSection?.querySelectorAll(".pi-item-card");
        const lastCard = cards?.[cards.length - 1];

        lastCard?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    });
  };

  const renameSection = (oldName, newName) => {
    const cleaned = cleanSectionName(newName);
    if (!cleaned) return;

    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item) =>
        cleanSectionName(item.section || "General") === oldName
          ? { ...item, section: cleaned }
          : item,
      ),
    }));

    if (activeServiceSection === oldName) {
      setActiveServiceSection(cleaned);
    }
  };

  const removeSection = (section) => {
    const sectionItems = form.items.filter(
      (item) => cleanSectionName(item.section || "General") === section,
    );

    if (
      !window.confirm(
        `Remove "${section}" section and its ${sectionItems.length} service item(s)?`,
      )
    ) {
      return;
    }

    setForm((previous) => {
      const remaining = previous.items.filter(
        (item) => cleanSectionName(item.section || "General") !== section,
      );

      return {
        ...previous,
        items: remaining.length ? remaining : [emptyItem("Architecture")],
      };
    });
  };

  const chooseService = (index, serviceId) => {
    const service = services.find((s) => s._id === serviceId);

    if (!service) {
      updateItem(index, {
        service: "",
        serviceName: "",
        serviceCode: "",
        description: "",
        hsnCode: "",
      });
      return;
    }

    updateItem(index, {
      service: service._id,
      serviceName: service.name,
      serviceCode: service.code || "",
      description: service.description || "",
      unit: service.unit || "Sq. Ft.",
      rate: Number(service.defaultRate || 0),
      hsnCode: service.sacCode || "",
      gstRate: Number(service.gstRate ?? 18),
    });
  };

  const validate = () => {
    if (!form.company) return "Select company";
    if (!form.client) return "Select client";
    if (!form.projectName) return "Select project";
    if (!form.documentDate) return "Select invoice date";
    if (!form.items?.length) return "Add at least one service";

    const invalidItem = form.items.some(
      (item) =>
        !cleanSectionName(item.section) ||
        !item.serviceName ||
        Number(item.quantity || 0) <= 0 ||
        Number(item.rate || 0) < 0,
    );

    if (invalidItem) return "Check service, quantity and rate";
    return "";
  };

  const waitForProformaPreview = async (timeoutMs = 3000) => {
    const startedAt = Date.now();

    while (!previewRef.current) {
      if (Date.now() - startedAt >= timeoutMs) {
        throw new Error(
          "Proforma Invoice preview could not be rendered for PDF generation.",
        );
      }

      await new Promise((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
    }

    // Allow layout and images to finish before html2canvas captures the preview.
    await new Promise((resolve) => setTimeout(resolve, 250));

    const images = Array.from(previewRef.current.querySelectorAll("img"));

    if (images.length) {
      await Promise.all(
        images.map(
          (image) =>
            new Promise((resolve) => {
              if (image.complete) {
                resolve();
                return;
              }

              const done = () => resolve();

              image.addEventListener("load", done, { once: true });
              image.addEventListener("error", done, { once: true });

              setTimeout(done, 1500);
            }),
        ),
      );
    }

    return previewRef.current;
  };

  const save = async () => {
    const validationMessage = validate();
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        type: "proforma",
        client: form.client || null,
        bank: form.bank || null,
        terms: automaticTerms,

        // Only one discount method is sent to backend at a time.
        overallDiscountType:
          form.discountMode === "total" ? form.overallDiscountType : "none",

        overallDiscountValue:
          form.discountMode === "total"
            ? Number(form.overallDiscountValue || 0)
            : 0,

        items: (form.items || []).map((item) => ({
          ...item,
          service: item.service || null,

          discountType:
            form.discountMode === "item" ? item.discountType : "none",

          discountValue:
            form.discountMode === "item" ? Number(item.discountValue || 0) : 0,
        })),
      };

      let savedDocument;

      if (editing) {
        const response = await api.put(`/sales/documents/${editing}`, payload, {
          headers,
        });

        savedDocument = response.data?.data || response.data;
      } else {
        const response = await api.post("/sales/documents", payload, {
          headers,
        });

        savedDocument = response.data?.data || response.data;
      }

      const savedDocumentId = savedDocument?._id || editing || "";

      // Always save the exact A4 Proforma Invoice PDF on the backend.
      // If Save was clicked from Edit Form, render Preview first,
      // wait for previewRef, generate the PDF and upload it.
      if (savedDocumentId) {
        if (!previewRef.current) {
          setShowPreview(true);
          await waitForProformaPreview();
        }

        const { blob } = await buildProformaPdfBlob();
        await saveProformaPdfToServer(savedDocumentId, blob);

        console.log(
          "[proforma-pdf] PDF uploaded successfully for document:",
          savedDocumentId,
        );
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      console.error("Save proforma invoice error:", error);
      alert(error.response?.data?.message || "Failed to save proforma invoice");
    } finally {
      setSaving(false);
    }
  };

  const edit = (document) => {
    setEditing(document._id);
    setShowPreview(false);
    setForm({
      ...document,
      company: document.company || "SDC",
      client: document.client?._id || document.client || "",
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => {
            const masterService = services.find(
              (service) =>
                String(service._id) === String(getServiceId(item.service)),
            );

            return {
              ...item,
              section: cleanSectionName(item.section || "General"),
              serviceCode: item.serviceCode || masterService?.code || "",
              description: item.description || masterService?.description || "",
              hsnCode: item.hsnCode || masterService?.sacCode || "",
              unit: item.unit || masterService?.unit || "Sq. Ft.",
              gstRate: Number(item.gstRate ?? masterService?.gstRate ?? 18),
            };
          })
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
    });
    setOpen(true);
  };

  const remove = async (document) => {
    if (
      !window.confirm(`Delete ${document.number || "this proforma invoice"}?`)
    ) {
      return;
    }

    try {
      await api.delete(`/sales/documents/${document._id}`, { headers });
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete");
    }
  };

  const convertToInvoice = async (document) => {
    if (!window.confirm("Convert this Proforma Invoice to Invoice?")) return;

    try {
      await api.post(
        `/sales/documents/${document._id}/convert`,
        { type: "invoice" },
        { headers },
      );
      alert("Converted to Invoice successfully");
      await load();
    } catch (error) {
      alert(error.response?.data?.message || "Conversion failed");
    }
  };

  const openDocumentForPdf = (document) => {
    setPdfAutoSaveDoneFor("");
    setEditing(document._id);
    setShowPreview(true);
    setForm({
      ...document,
      company: document.company || "SDC",
      client: document.client?._id || document.client || "",
      bank: document.bank?._id || document.bank || "",
      projectName: document.projectName || "",
      subject: document.subject || "",
      documentDate: document.documentDate?.slice(0, 10) || today(),
      dueDate: document.dueDate?.slice?.(0, 10) || "",
      taxMode: document.taxMode || "CGST_SGST",
      discountMode:
        document.discountMode ||
        (document.overallDiscountType &&
        document.overallDiscountType !== "none" &&
        Number(document.overallDiscountValue || 0) > 0
          ? "total"
          : "item"),
      overallDiscountType: document.overallDiscountType || "none",
      overallDiscountValue: Number(document.overallDiscountValue || 0),
      items: document.items?.length
        ? document.items.map((item) => {
            const masterService = services.find(
              (service) =>
                String(service._id) === String(getServiceId(item.service)),
            );

            return {
              ...item,
              section: cleanSectionName(item.section || "General"),
              serviceCode: item.serviceCode || masterService?.code || "",
              description: item.description || masterService?.description || "",
              hsnCode: item.hsnCode || masterService?.sacCode || "",
              unit: item.unit || masterService?.unit || "Sq. Ft.",
              gstRate: Number(item.gstRate ?? masterService?.gstRate ?? 18),
            };
          })
        : [emptyItem("Architecture")],
      terms: document.terms || [],
      status: document.status || "Draft",
      paidAmount: Number(document.paidAmount || 0),
      number: document.number || "",
    });
    setOpen(true);
  };

  const buildProformaPdfBlob = async () => {
    if (!previewRef.current) {
      throw new Error("Proforma Invoice preview is not available");
    }

    const canvas = await html2canvas(previewRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imageData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 8;
    const usableWidth = pageWidth - margin * 2;
    const imageHeight = (canvas.height * usableWidth) / canvas.width;

    let heightLeft = imageHeight;
    let position = margin;

    pdf.addImage(imageData, "PNG", margin, position, usableWidth, imageHeight);

    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      position = heightLeft - imageHeight + margin;

      pdf.addPage();
      pdf.addImage(
        imageData,
        "PNG",
        margin,
        position,
        usableWidth,
        imageHeight,
      );

      heightLeft -= pageHeight - margin * 2;
    }

    return {
      pdf,
      blob: pdf.output("blob"),
    };
  };

  const saveProformaPdfToServer = async (documentId, pdfBlob) => {
    if (!documentId || !pdfBlob) return;

    const fd = new FormData();

    const safeNumber = String(form.number || "PROFORMA-INVOICE").replace(
      /[^\w-]+/g,
      "-",
    );

    fd.append(
      "pdf",
      new File([pdfBlob], `${safeNumber}.pdf`, {
        type: "application/pdf",
      }),
    );

    await api.post(`/sales/documents/${documentId}/pdf`, fd, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  };

  // Save a stored PDF automatically whenever an existing Proforma Invoice
  // is opened in Preview / PDF mode.
  useEffect(() => {
    if (!showPreview || !editing || !previewRef.current) return;
    if (pdfAutoSaving) return;
    if (pdfAutoSaveDoneFor === String(editing)) return;

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        if (cancelled || !previewRef.current) return;

        setPdfAutoSaving(true);

        const { blob } = await buildProformaPdfBlob();
        await saveProformaPdfToServer(editing, blob);

        if (!cancelled) {
          setPdfAutoSaveDoneFor(String(editing));
        }
      } catch (error) {
        console.error("Automatic Proforma Invoice PDF save failed:", error);
      } finally {
        if (!cancelled) {
          setPdfAutoSaving(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, editing]);

  const downloadPdf = async () => {
    if (!previewRef.current) return;

    try {
      setDownloading(true);

      const { pdf, blob } = await buildProformaPdfBlob();

      // Keep the latest Proforma Invoice PDF on the backend as well.
      if (editing) {
        await saveProformaPdfToServer(editing, blob);
      }

      const safeNumber = String(form.number || "PROFORMA-INVOICE").replace(
        /[^\w-]+/g,
        "-",
      );

      pdf.save(`${safeNumber}.pdf`);
    } catch (error) {
      console.error("PDF error:", error);

      alert(
        error.response?.data?.message ||
          error.message ||
          "The Proforma Invoice PDF could not be generated or saved.",
      );
    } finally {
      setDownloading(false);
    }
  };

  const itemAmount = (item) => {
    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;
    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.max(0, base - Math.min(discount, base));
  };

  const itemDiscountAmount = (item) => {
    if ((form.discountMode || "item") !== "item") return 0;

    const base = Number(item.quantity || 0) * Number(item.rate || 0);

    let discount = 0;

    if (item.discountType === "percentage") {
      discount = (base * Number(item.discountValue || 0)) / 100;
    } else if (item.discountType === "amount") {
      discount = Number(item.discountValue || 0);
    }

    return Math.min(Math.max(discount, 0), base);
  };

  const itemDiscountLabel = (item) => {
    if ((form.discountMode || "item") !== "item") return "-";

    const discountAmount = itemDiscountAmount(item);

    if (!discountAmount) return "-";

    if (item.discountType === "percentage") {
      return `${Number(item.discountValue || 0)}% (₹ ${money(discountAmount)})`;
    }

    if (item.discountType === "amount") {
      return `₹ ${money(discountAmount)}`;
    }

    return "-";
  };

  const itemTaxAmount = (item) => {
    if (form.taxMode === "NONE") return 0;

    const taxable = itemAmount(item);
    return (taxable * Number(item.gstRate || 0)) / 100;
  };

  const itemFinalAmount = (item) => itemAmount(item) + itemTaxAmount(item);

  const totalDiscountLabel = () => {
    if (form.discountMode !== "total" || !totals.overallDiscount) {
      return "";
    }

    if (form.overallDiscountType === "percentage") {
      return `${Number(form.overallDiscountValue || 0)}% (₹ ${money(
        totals.overallDiscount,
      )})`;
    }

    return `₹ ${money(totals.overallDiscount)}`;
  };

  const availableFinancialYears = useMemo(() => {
    const years = docs
      .map((document) => {
        if (!document.documentDate) return null;
        return getFinancialYearForDate(document.documentDate);
      })
      .filter((year) => year !== null);

    years.push(getCurrentFinancialYearStart());

    return [...new Set(years)].sort((a, b) => b - a);
  }, [docs]);

  const filteredDocs = useMemo(() => {
    return docs.filter((document) => {
      if (!document.documentDate) return false;

      const date = new Date(document.documentDate);
      if (Number.isNaN(date.getTime())) return false;

      const financialYearStart = Number(
        filterFinancialYear || getCurrentFinancialYearStart(),
      );

      if (filterBy === "month") {
        const selectedMonth = Number(filterMonth || 4);
        const calendarYear = getCalendarYearForFinancialMonth(
          financialYearStart,
          selectedMonth,
        );

        if (date.getFullYear() !== calendarYear) return false;
        if (date.getMonth() + 1 !== selectedMonth) return false;
      }

      if (filterBy === "quarter") {
        const quarter = Number(filterQuarter || 1);

        const quarterRanges = {
          1: {
            from: new Date(financialYearStart, 3, 1),
            to: new Date(financialYearStart, 5, 30, 23, 59, 59, 999),
          },
          2: {
            from: new Date(financialYearStart, 6, 1),
            to: new Date(financialYearStart, 8, 30, 23, 59, 59, 999),
          },
          3: {
            from: new Date(financialYearStart, 9, 1),
            to: new Date(financialYearStart, 11, 31, 23, 59, 59, 999),
          },
          4: {
            from: new Date(financialYearStart + 1, 0, 1),
            to: new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999),
          },
        };

        const range = quarterRanges[quarter] || quarterRanges[1];
        if (date < range.from || date > range.to) return false;
      }

      if (filterBy === "year") {
        const from = new Date(financialYearStart, 3, 1);
        const to = new Date(financialYearStart + 1, 2, 31, 23, 59, 59, 999);

        if (date < from || date > to) return false;
      }

      if (filterBy === "total") {
        const total = Number(document.grandTotal || 0);

        if (filterMinTotal !== "" && total < Number(filterMinTotal))
          return false;
        if (filterMaxTotal !== "" && total > Number(filterMaxTotal))
          return false;
      }

      if (filterBy === "custom") {
        const documentDay = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );

        if (filterFromDate) {
          const [fy, fm, fd] = filterFromDate.split("-").map(Number);
          const from = new Date(fy, fm - 1, fd);
          if (documentDay < from) return false;
        }

        if (filterToDate) {
          const [ty, tm, td] = filterToDate.split("-").map(Number);
          const to = new Date(ty, tm - 1, td);
          if (documentDay > to) return false;
        }
      }

      return true;
    });
  }, [
    docs,
    filterBy,
    filterFinancialYear,
    filterMonth,
    filterQuarter,
    filterFromDate,
    filterToDate,
    filterMinTotal,
    filterMaxTotal,
  ]);

  const handleFilterByChange = (value) => {
    const currentMonth = currentFilterDate.getMonth() + 1;

    setFilterBy(value);

    if (value === "month") {
      setFilterFinancialYear(String(getCurrentFinancialYearStart()));
      setFilterMonth(String(currentMonth));
      setFilterQuarter("");
      setFilterFromDate("");
      setFilterToDate("");
      setFilterMinTotal("");
      setFilterMaxTotal("");
    } else if (value === "quarter") {
      setFilterFinancialYear(String(getCurrentFinancialYearStart()));
      setFilterMonth("");
      setFilterQuarter(
        currentMonth >= 4
          ? String(Math.floor((currentMonth - 4) / 3) + 1)
          : "4",
      );
      setFilterFromDate("");
      setFilterToDate("");
      setFilterMinTotal("");
      setFilterMaxTotal("");
    } else if (value === "year") {
      setFilterFinancialYear(String(getCurrentFinancialYearStart()));
      setFilterMonth("");
      setFilterQuarter("");
      setFilterFromDate("");
      setFilterToDate("");
      setFilterMinTotal("");
      setFilterMaxTotal("");
    } else if (value === "total") {
      setFilterFinancialYear("");
      setFilterMonth("");
      setFilterQuarter("");
      setFilterFromDate("");
      setFilterToDate("");
      setFilterMinTotal("");
      setFilterMaxTotal("");
    } else {
      setFilterFinancialYear("");
      setFilterMonth("");
      setFilterQuarter("");
      setFilterFromDate("");
      setFilterToDate("");
      setFilterMinTotal("");
      setFilterMaxTotal("");
    }
  };

  return (
    <div className="pi-page">
      <div className="pi-topbar">
        <div>
          <h1>Proforma Invoices</h1>
          <p>
            Create, manage, preview and download professional proforma invoices.
          </p>
        </div>

        <button
          className="pi-btn primary"
          onClick={() => {
            reset();
            setShowPreview(false);
            setOpen(true);
          }}
        >
          + Create Proforma Invoice
        </button>
      </div>

      <div className="pi-filter-card pi-filter-reference-style">
        <div className="pi-filter-copy">
          <h3>Proforma Invoice Overview</h3>
          <p>
            View proforma invoices month-wise, quarterly, financial-year-wise,
            by total amount or custom date range.
          </p>
        </div>

        <div className={`pi-filter-reference-grid pi-filter-mode-${filterBy}`}>
          <label className="pi-filter-reference-field">
            <span>View By</span>
            <SearchableSelect
              value={filterBy}
              options={[
                { value: "month", label: "Month Wise" },
                { value: "quarter", label: "Quarter Wise" },
                { value: "year", label: "Financial Year Wise" },
                { value: "custom", label: "Custom Date" },
                { value: "total", label: "Total Wise" },
              ]}
              onChange={handleFilterByChange}
              placeholder="Select View"
              isClearable={false}
            />
          </label>

          {(filterBy === "month" ||
            filterBy === "quarter" ||
            filterBy === "year") && (
            <label className="pi-filter-reference-field">
              <span>Financial Year</span>
              <SearchableSelect
                value={filterFinancialYear}
                options={availableFinancialYears.map((year) => ({
                  value: String(year),
                  label: getFinancialYearLabel(year),
                }))}
                onChange={setFilterFinancialYear}
                placeholder="Select Financial Year"
                isClearable={false}
              />
            </label>
          )}

          {filterBy === "month" && (
            <label className="pi-filter-reference-field">
              <span>Month</span>
              <SearchableSelect
                value={filterMonth}
                options={[
                  { value: "", label: "All Months" },
                  { value: "1", label: "January" },
                  { value: "2", label: "February" },
                  { value: "3", label: "March" },
                  { value: "4", label: "April" },
                  { value: "5", label: "May" },
                  { value: "6", label: "June" },
                  { value: "7", label: "July" },
                  { value: "8", label: "August" },
                  { value: "9", label: "September" },
                  { value: "10", label: "October" },
                  { value: "11", label: "November" },
                  { value: "12", label: "December" },
                ]}
                onChange={setFilterMonth}
                placeholder="Search Month"
              />
            </label>
          )}

          {filterBy === "quarter" && (
            <label className="pi-filter-reference-field">
              <span>Quarter</span>
              <SearchableSelect
                value={filterQuarter}
                options={[
                  { value: "", label: "All Quarters" },
                  { value: "1", label: "Q1 (Apr - Jun)" },
                  { value: "2", label: "Q2 (Jul - Sep)" },
                  { value: "3", label: "Q3 (Oct - Dec)" },
                  { value: "4", label: "Q4 (Jan - Mar)" },
                ]}
                onChange={setFilterQuarter}
                placeholder="Search Quarter"
              />
            </label>
          )}

          {filterBy === "total" && (
            <>
              <label className="pi-filter-reference-field">
                <span>Min Total</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="₹ Minimum"
                  value={filterMinTotal}
                  onChange={(e) => setFilterMinTotal(e.target.value)}
                />
              </label>

              <label className="pi-filter-reference-field">
                <span>Max Total</span>
                <input
                  type="number"
                  min={filterMinTotal || "0"}
                  step="0.01"
                  placeholder="₹ Maximum"
                  value={filterMaxTotal}
                  onChange={(e) => setFilterMaxTotal(e.target.value)}
                />
              </label>
            </>
          )}

          {filterBy === "custom" && (
            <>
              <label className="pi-filter-reference-field">
                <span>From Date</span>
                <input
                  type="date"
                  value={filterFromDate}
                  max={filterToDate || undefined}
                  onChange={(e) => setFilterFromDate(e.target.value)}
                />
              </label>

              <label className="pi-filter-reference-field">
                <span>To Date</span>
                <input
                  type="date"
                  value={filterToDate}
                  min={filterFromDate || undefined}
                  onChange={(e) => setFilterToDate(e.target.value)}
                />
              </label>
            </>
          )}

          <div className="pi-filter-summary">
            <div className="pi-filter-stat">
              <span>Invoices</span>
              <strong>{filteredDocs.length}</strong>
            </div>

            <div className="pi-filter-stat total">
              <span>Total</span>
              <strong>
                ₹{" "}
                {money(
                  filteredDocs.reduce(
                    (sum, document) => sum + Number(document.grandTotal || 0),
                    0,
                  ),
                )}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div className="pi-card">
        {loading ? (
          <div className="pi-empty">Loading...</div>
        ) : docs.length === 0 ? (
          <div className="pi-empty">No proforma invoices found.</div>
        ) : filteredDocs.length === 0 ? (
          <div className="pi-empty">
            No proforma invoices found for the selected period.
          </div>
        ) : (
          <div className="pi-table-wrap">
            <table className="pi-table">
              <thead>
                <tr>
                  <th>PI No.</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocs.map((document) => (
                  <tr key={document._id}>
                    <td className="pi-number">{document.number}</td>
                    <td>{document.client?.companyName || "-"}</td>
                    <td>
                      {document.documentDate
                        ? new Date(document.documentDate).toLocaleDateString(
                            "en-IN",
                          )
                        : "-"}
                    </td>
                    <td>{document.projectName || "-"}</td>
                    <td>₹ {money(document.grandTotal)}</td>
                    <td>
                      <span className="pi-status">
                        {document.status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <div className="pi-action-row">
                        <button
                          className="pi-link"
                          onClick={() => edit(document)}
                        >
                          Edit
                        </button>
                        <button
                          className="pi-link"
                          onClick={() => openDocumentForPdf(document)}
                        >
                          Preview / PDF
                        </button>
                        <button
                          className="pi-link"
                          onClick={() => convertToInvoice(document)}
                        >
                          Convert Invoice
                        </button>
                        <button
                          className="pi-link danger"
                          onClick={() => remove(document)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <div className="pi-modal-overlay">
          <div className="pi-modal">
            <div className="pi-modal-head">
              <div>
                <h2>{editing ? "Edit" : "Create"} Proforma Invoice</h2>
                <p>
                  {showPreview
                    ? "Preview the final A4 Proforma Invoice."
                    : "Fill the Proforma Invoice details."}
                </p>
              </div>
              <div className="pi-modal-head-actions">
                <button
                  type="button"
                  className={`pi-mode-btn ${!showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(false)}
                >
                  Edit Form
                </button>

                <button
                  type="button"
                  className={`pi-mode-btn ${showPreview ? "active" : ""}`}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </button>

                <button className="pi-close" onClick={() => setOpen(false)}>
                  ✕
                </button>
              </div>
            </div>

            <div className="pi-editor">
              {!showPreview && (
                <div className="pi-form-panel">
                  <section className="pi-section">
                    <h3>Basic Information</h3>

                    <div className="pi-grid four basic-info-grid">
                      <label>
                        Company
                        <SearchableSelect
                          value={form.company}
                          options={[{ value: "SDC", label: "SDC" }]}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              company: value || "SDC",
                              bank: "",
                            })
                          }
                          placeholder="Search Company"
                          isClearable={false}
                        />
                      </label>

                      <label>
                        Client
                        <SearchableSelect
                          value={form.client}
                          options={clients.map((client) => ({
                            value: client._id,
                            label:
                              client.companyName ||
                              client.name ||
                              "Unnamed Client",
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              client: value,
                              projectName: "",
                            })
                          }
                          placeholder="Search / Select Client"
                        />
                      </label>

                      <label>
                        PI Date
                        <input
                          type="date"
                          value={form.documentDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, documentDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Due Date
                        <input
                          type="date"
                          value={form.dueDate || ""}
                          onChange={(e) =>
                            setForm({ ...form, dueDate: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Project
                        <SearchableSelect
                          value={form.projectName || ""}
                          options={filteredProjects.map((project) => ({
                            value: project.name,
                            label: `${project.name || "Unnamed Project"}${
                              project.category ? ` — ${project.category}` : ""
                            }`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              projectName: value,
                            })
                          }
                          placeholder="Search / Select Project"
                        />
                      </label>

                      <label>
                        Subject
                        <input
                          value={form.subject || ""}
                          placeholder="Subject / Scope"
                          onChange={(e) =>
                            setForm({ ...form, subject: e.target.value })
                          }
                        />
                      </label>

                      <label>
                        Bank
                        <SearchableSelect
                          value={form.bank || ""}
                          options={filteredBanks.map((bank) => ({
                            value: bank._id,
                            label: `${bank.bankName || "Bank"} - ${bank.accountNumber || ""}`,
                          }))}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              bank: value,
                            })
                          }
                          placeholder="Search / Select Bank"
                        />
                      </label>

                      <label>
                        Tax Type
                        <SearchableSelect
                          value={form.taxMode}
                          options={[
                            { value: "CGST_SGST", label: "CGST + SGST" },
                            { value: "IGST", label: "IGST" },
                            { value: "NONE", label: "No GST" },
                          ]}
                          onChange={(value) =>
                            setForm({
                              ...form,
                              taxMode: value || "CGST_SGST",
                            })
                          }
                          placeholder="Search Tax Type"
                          isClearable={false}
                        />
                      </label>
                    </div>
                  </section>

                  <section className="pi-section">
                    <div className="pi-section-title-row">
                      <div>
                        <h3>Service Sections</h3>
                        <p className="pi-section-help">
                          Add Architecture, Interior, Structure, BIM or any
                          other service section in the same Proforma Invoice.
                        </p>
                      </div>
                    </div>

                    <div className="pi-add-section-bar">
                      <input
                        value={newSectionName}
                        placeholder="New section name e.g. Interior"
                        onChange={(e) => setNewSectionName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSection();
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={addSection}
                      >
                        + Add Section
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) =>
                                section.toLowerCase() === "architecture",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Architecture"),
                              ],
                            }));
                          }
                          setActiveServiceSection("Architecture");
                        }}
                      >
                        Architecture
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) => section.toLowerCase() === "interior",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [...previous.items, emptyItem("Interior")],
                            }));
                          }
                          setActiveServiceSection("Interior");
                        }}
                      >
                        Interior
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => {
                          if (
                            !sections.some(
                              (section) =>
                                section.toLowerCase() === "structure",
                            )
                          ) {
                            setForm((previous) => ({
                              ...previous,
                              items: [
                                ...previous.items,
                                emptyItem("Structure"),
                              ],
                            }));
                          }
                          setActiveServiceSection("Structure");
                        }}
                      >
                        Structure
                      </button>
                    </div>

                    <div className="pi-service-sections">
                      {sections.map((section) => {
                        const sectionRows = form.items
                          .map((item, index) => ({ item, index }))
                          .filter(
                            ({ item }) =>
                              cleanSectionName(item.section || "General") ===
                              section,
                          );

                        const sectionTotal = sectionRows.reduce(
                          (sum, { item }) => sum + itemFinalAmount(item),
                          0,
                        );

                        return (
                          <div
                            className="pi-service-section"
                            key={section}
                            data-service-section={section}
                            onMouseDown={() => setActiveServiceSection(section)}
                          >
                            <div className="pi-service-section-head">
                              <div className="pi-section-name-wrap">
                                <span className="pi-section-badge">
                                  SECTION
                                </span>
                                <input
                                  className="pi-section-name-input"
                                  value={section}
                                  onChange={(e) =>
                                    renameSection(section, e.target.value)
                                  }
                                />
                              </div>

                              <div className="pi-service-section-actions">
                                <button
                                  ref={
                                    section === activeServiceSection
                                      ? activeAddServiceButtonRef
                                      : null
                                  }
                                  type="button"
                                  className="pi-btn"
                                  onClick={() => addServiceToSection(section)}
                                >
                                  + Add Service
                                </button>

                                {sections.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-btn danger-outline"
                                    onClick={() => removeSection(section)}
                                  >
                                    Remove Section
                                  </button>
                                )}
                              </div>
                            </div>

                            {sectionRows.map(({ item, index }) => (
                              <div
                                className="pi-item-card"
                                key={`${section}-${index}`}
                              >
                                <div className="pi-grid item-grid">
                                  <label className="wide">
                                    Service
                                    <SearchableSelect
                                      value={item.service || ""}
                                      options={services.map((service) => ({
                                        value: service._id,
                                        label: `${service.name || "Unnamed Service"}${
                                          service.code
                                            ? ` — ${service.code}`
                                            : ""
                                        }`,
                                      }))}
                                      onChange={(value) =>
                                        chooseService(index, value)
                                      }
                                      placeholder="Search / Select Service"
                                    />
                                  </label>

                                  <label>
                                    Service Code
                                    <input
                                      type="text"
                                      value={item.serviceCode || ""}
                                      placeholder="Auto fetched"
                                      readOnly
                                    />
                                  </label>

                                  <label>
                                    Qty
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          quantity: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    Unit
                                    <SearchableSelect
                                      value={item.unit || "Sq. Ft."}
                                      options={UNIT_OPTIONS.map((unit) => ({
                                        value: unit,
                                        label: unit,
                                      }))}
                                      onChange={(value) =>
                                        updateItem(index, {
                                          unit: value || "Sq. Ft.",
                                        })
                                      }
                                      placeholder="Search Unit"
                                      isClearable={false}
                                    />
                                  </label>

                                  <label>
                                    Rate
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.rate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          rate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  {form.discountMode === "item" ? (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-input">
                                        <SearchableSelect
                                          value={item.discountType}
                                          options={[
                                            { value: "none", label: "None" },
                                            { value: "percentage", label: "%" },
                                            { value: "amount", label: "₹" },
                                          ]}
                                          onChange={(value) =>
                                            updateItem(index, {
                                              discountType: value || "none",
                                            })
                                          }
                                          placeholder="Search"
                                          isClearable={false}
                                        />

                                        <input
                                          type="number"
                                          min="0"
                                          value={item.discountValue}
                                          onChange={(e) =>
                                            updateItem(index, {
                                              discountValue: Number(
                                                e.target.value,
                                              ),
                                            })
                                          }
                                        />
                                      </div>
                                    </label>
                                  ) : (
                                    <label>
                                      Item Discount
                                      <div className="pi-discount-disabled">
                                        Total discount selected
                                      </div>
                                    </label>
                                  )}

                                  <label>
                                    GST %
                                    <input
                                      type="number"
                                      min="0"
                                      value={item.gstRate}
                                      onChange={(e) =>
                                        updateItem(index, {
                                          gstRate: Number(e.target.value),
                                        })
                                      }
                                    />
                                  </label>

                                  <label>
                                    Amount
                                    <div className="pi-readonly-amount">
                                      ₹ {money(itemFinalAmount(item))}
                                    </div>
                                  </label>
                                </div>

                                <label className="pi-description-label">
                                  Description
                                  <textarea
                                    value={item.description || ""}
                                    onChange={(e) =>
                                      updateItem(index, {
                                        description: e.target.value,
                                      })
                                    }
                                  />
                                </label>

                                {sectionRows.length > 1 && (
                                  <button
                                    type="button"
                                    className="pi-remove-item"
                                    onClick={() =>
                                      setForm((previous) => ({
                                        ...previous,
                                        items: previous.items.filter(
                                          (_, itemIndex) => itemIndex !== index,
                                        ),
                                      }))
                                    }
                                  >
                                    Remove Service
                                  </button>
                                )}
                              </div>
                            ))}

                            <div className="pi-service-section-total">
                              <span>{section} Total</span>
                              <strong>₹ {money(sectionTotal)}</strong>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pi-service-grand-total">
                      <div>
                        <span>Overall Total</span>
                        <small>Including applicable GST</small>
                      </div>
                      <strong>₹ {money(totals.grandTotal)}</strong>
                    </div>
                  </section>

                  {showFloatingAddService && (
                    <div className="pi-floating-add-service">
                      <select
                        className="pi-floating-section-select"
                        value={
                          sections.includes(activeServiceSection)
                            ? activeServiceSection
                            : sections[0] || "Architecture"
                        }
                        onChange={(e) =>
                          setActiveServiceSection(e.target.value)
                        }
                        aria-label="Select service section"
                      >
                        {sections.map((section) => (
                          <option key={section} value={section}>
                            {section}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        className="pi-floating-add-service-btn"
                        onClick={() =>
                          addServiceToSection(
                            activeServiceSection ||
                              sections[0] ||
                              "Architecture",
                          )
                        }
                      >
                        + Add Service
                      </button>
                    </div>
                  )}

                  <section className="pi-section">
                    <h3>Discount & Terms</h3>

                    <div className="pi-discount-mode-box">
                      <div className="pi-discount-mode-title">
                        Apply Discount On
                      </div>

                      <div className="pi-discount-mode-options">
                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "item" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="item"
                            checked={form.discountMode === "item"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "item",
                                overallDiscountType: "none",
                                overallDiscountValue: 0,
                              }))
                            }
                          />
                          <div>
                            <strong>Item Wise Discount</strong>
                            <span>
                              Discount can be different for each service item.
                            </span>
                          </div>
                        </label>

                        <label
                          className={`pi-discount-mode-card ${
                            form.discountMode === "total" ? "active" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="discountMode"
                            value="total"
                            checked={form.discountMode === "total"}
                            onChange={() =>
                              setForm((previous) => ({
                                ...previous,
                                discountMode: "total",
                                items: previous.items.map((item) => ({
                                  ...item,
                                  discountType: "none",
                                  discountValue: 0,
                                })),
                              }))
                            }
                          />
                          <div>
                            <strong>Total Invoice Discount</strong>
                            <span>
                              One discount is applied to the total service
                              value.
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {form.discountMode === "total" && (
                      <div className="pi-grid two pi-total-discount-fields">
                        <label>
                          Total Discount Type
                          <SearchableSelect
                            value={form.overallDiscountType}
                            options={[
                              { value: "none", label: "No Discount" },
                              { value: "percentage", label: "Percentage (%)" },
                              { value: "amount", label: "Fixed Amount (₹)" },
                            ]}
                            onChange={(value) =>
                              setForm({
                                ...form,
                                overallDiscountType: value || "none",
                              })
                            }
                            placeholder="Search Discount Type"
                            isClearable={false}
                          />
                        </label>

                        <label>
                          Total Discount Value
                          <input
                            type="number"
                            min="0"
                            value={form.overallDiscountValue}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                overallDiscountValue: Number(e.target.value),
                              })
                            }
                          />
                        </label>
                      </div>
                    )}

                    <div className="pi-auto-terms">
                      <div className="pi-auto-terms-head">
                        <div>
                          <h4> Terms & Conditions</h4>
                        </div>
                      </div>

                      {automaticTerms.length ? (
                        <ol className="pi-continuous-terms-list form-list">
                          {automaticTerms.map((term, index) => (
                            <li key={index}>{term}</li>
                          ))}
                        </ol>
                      ) : (
                        <div className="pi-no-auto-terms">
                          Add Terms & Conditions in Terms Master for
                          Architecture, Interior or Structure.
                        </div>
                      )}
                    </div>
                  </section>

                  <div className="pi-form-actions">
                    <button
                      className="pi-btn"
                      type="button"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </button>

                    <button
                      className="pi-btn preview"
                      type="button"
                      onClick={() => setShowPreview(true)}
                    >
                      Preview
                    </button>

                    <button
                      className="pi-btn primary"
                      type="button"
                      onClick={save}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Proforma Invoice"}
                    </button>
                  </div>
                </div>
              )}

              {showPreview && (
                <div className="pi-preview-panel preview-only">
                  <div className="pi-preview-toolbar">
                    <div>
                      <div className="pi-preview-label">A4 Preview</div>
                      <span>Review before saving or downloading PDF.</span>
                    </div>

                    <div className="pi-preview-toolbar-actions">
                      <button
                        type="button"
                        className="pi-btn"
                        onClick={() => setShowPreview(false)}
                      >
                        ← Back to Edit
                      </button>

                      <button
                        type="button"
                        className="pi-btn"
                        onClick={downloadPdf}
                        disabled={downloading}
                      >
                        {downloading ? "Generating PDF..." : "Download PDF"}
                      </button>

                      <button
                        type="button"
                        className="pi-btn primary"
                        onClick={save}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save Proforma Invoice"}
                      </button>
                    </div>
                  </div>

                  <div className="pi-preview-stage">
                    <div className="pi-a4" ref={previewRef}>
                      <header className="pi-doc-header">
                        <div className="pi-brand">
                          {sdcLogo ? (
                            <img
                              src={sdcLogo}
                              alt="Company logo"
                              className="pi-logo"
                            />
                          ) : (
                            <div className="pi-logo-placeholder">LOGO</div>
                          )}

                          <div>
                            <h2>{companyInfo.name}</h2>
                            <p>{companyInfo.address}</p>
                            {companyInfo.email && <p>{companyInfo.email}</p>}
                            {companyInfo.phone && <p>{companyInfo.phone}</p>}
                            {companyInfo.gstin && (
                              <p>GSTIN: {companyInfo.gstin}</p>
                            )}
                          </div>
                        </div>

                        <div className="pi-doc-title">
                          <h1>PROFORMA INVOICE</h1>
                          <p>
                            <strong>PI No:</strong>{" "}
                            {form.number || "Will be generated on save"}
                          </p>
                          <p>
                            <strong>Date:</strong> {form.documentDate || "-"}
                          </p>
                          <p>
                            <strong>Due Date:</strong> {form.dueDate || "-"}
                          </p>
                        </div>
                      </header>

                      <div className="pi-blue-line" />

                      <section className="pi-party-grid">
                        <div>
                          <span className="pi-small-title">BILL TO</span>
                          <h3>
                            {selectedClient?.companyName || "Select Client"}
                          </h3>
                          <p>{selectedClient?.contactPerson || ""}</p>
                          <p>{formatAddress(selectedClient?.billingAddress)}</p>
                          <p>{selectedClient?.email || ""}</p>
                          <p>{selectedClient?.phone || ""}</p>
                          {selectedClient?.gstin && (
                            <p>GSTIN: {selectedClient.gstin}</p>
                          )}
                        </div>

                        <div className="pi-project-box">
                          <p>
                            <strong>Project:</strong> {form.projectName || "-"}
                          </p>
                          {selectedProject?.category && (
                            <p>
                              <strong>Category:</strong>{" "}
                              {selectedProject.category}
                            </p>
                          )}
                          <p>
                            <strong>Subject:</strong> {form.subject || "-"}
                          </p>
                        </div>
                      </section>

                      <div className="pi-doc-items">
                        <div className="pi-doc-items-head">
                          <div>#</div>
                          <div>Service / Description</div>
                          <div>Qty</div>
                          <div>Unit</div>
                          <div>Unit Rate</div>
                          <div>Discount</div>
                          <div>GST</div>
                          <div>Amount</div>
                        </div>

                        {sections.map((section) => {
                          const sectionRows = form.items
                            .map((item, index) => ({ item, index }))
                            .filter(
                              ({ item }) =>
                                cleanSectionName(item.section || "General") ===
                                section,
                            );

                          const sectionSubtotal = sectionRows.reduce(
                            (sum, { item }) => sum + itemAmount(item),
                            0,
                          );

                          return (
                            <div
                              className="pi-doc-service-section"
                              key={section}
                            >
                              <div className="pi-doc-section-title">
                                {section}
                              </div>

                              {sectionRows.map(({ item, index }) => (
                                <div
                                  className="pi-doc-item-row"
                                  key={`${section}-${index}`}
                                >
                                  <div>{index + 1}</div>

                                  <div className="pi-doc-service-cell">
                                    <strong>
                                      {item.serviceName || "Service"}
                                    </strong>

                                    {item.description && (
                                      <p>{item.description}</p>
                                    )}
                                  </div>

                                  <div>{item.quantity}</div>

                                  <div>{item.unit}</div>

                                  <div className="pi-doc-number-cell">
                                    ₹ {money(item.rate)}
                                  </div>

                                  <div className="pi-doc-discount-cell">
                                    {itemDiscountLabel(item)}
                                  </div>

                                  <div className="pi-doc-gst-cell">
                                    {Number(item.gstRate || 0)}%
                                  </div>

                                  <div className="pi-doc-number-cell">
                                    ₹ {money(itemFinalAmount(item))}
                                  </div>
                                </div>
                              ))}

                              <div className="pi-doc-section-total">
                                <span>Section Taxable Subtotal</span>
                                <strong>₹ {money(sectionSubtotal)}</strong>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <section className="pi-doc-bottom">
                        <div className="pi-terms-box">
                          <h4>Terms & Conditions</h4>

                          {automaticTerms.length ? (
                            <ol className="pi-continuous-terms-list">
                              {automaticTerms.map((term, index) => (
                                <li key={index}>{term}</li>
                              ))}
                            </ol>
                          ) : (
                            <p>
                              Terms will appear automatically from Terms Master.
                            </p>
                          )}
                        </div>

                        <div className="pi-total-box">
                          <div>
                            <span>Subtotal</span>
                            <b>₹ {money(totals.subtotal)}</b>
                          </div>
                          {form.discountMode === "item" &&
                            totals.itemDiscount > 0 && (
                              <div>
                                <span>Item Discount</span>
                                <b>- ₹ {money(totals.itemDiscount)}</b>
                              </div>
                            )}

                          {form.discountMode === "total" &&
                            totals.overallDiscount > 0 && (
                              <div>
                                <span>Total Discount</span>
                                <b>- {totalDiscountLabel()}</b>
                              </div>
                            )}
                          <div>
                            <span>Taxable Amount</span>
                            <b>₹ {money(totals.taxableAmount)}</b>
                          </div>

                          {form.taxMode === "CGST_SGST" && (
                            <>
                              <div>
                                <span>CGST</span>
                                <b>₹ {money(totals.cgst)}</b>
                              </div>
                              <div>
                                <span>SGST</span>
                                <b>₹ {money(totals.sgst)}</b>
                              </div>
                            </>
                          )}

                          {form.taxMode === "IGST" && (
                            <div>
                              <span>IGST</span>
                              <b>₹ {money(totals.igst)}</b>
                            </div>
                          )}

                          <div className="pi-grand-total">
                            <span>Grand Total</span>
                            <b>₹ {money(totals.grandTotal)}</b>
                          </div>
                        </div>
                      </section>

                      <section className="pi-bank-box">
                        <h4>Bank Details</h4>
                        {selectedBank ? (
                          <div className="pi-bank-grid">
                            <p>
                              <strong>Bank:</strong>{" "}
                              {selectedBank.bankName || "-"}
                            </p>
                            <p>
                              <strong>A/C Name:</strong>{" "}
                              {selectedBank.accountName || "-"}
                            </p>
                            <p>
                              <strong>A/C No:</strong>{" "}
                              {selectedBank.accountNumber || "-"}
                            </p>
                            <p>
                              <strong>IFSC:</strong> {selectedBank.ifsc || "-"}
                            </p>
                            {selectedBank.swiftCode && (
                              <p>
                                <strong>SWIFT:</strong> {selectedBank.swiftCode}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p>Select a bank account.</p>
                        )}
                      </section>

                      <footer className="pi-doc-footer">
                        <div>
                          <p>
                            This is a Proforma Invoice and not a tax invoice /
                            payment receipt.
                          </p>
                        </div>

                        <div className="pi-signature">
                          <img
                            src={authorizedSignature}
                            alt="Authorized Signatory"
                            className="pi-signature-image"
                          />
                          <div className="pi-sign-line" />
                          <strong>Authorized Signatory</strong>
                          <span>{companyInfo.name}</span>
                        </div>
                      </footer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProformaInvoicePage;
